// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../api/api';

export enum UserLevel {
  UNLIMITED = 'unlimited',
  BASE = 'base',
  PRO = 'pro',
  EXPERT = 'expert',
  ENTERPRISE = 'enterprise',
  FREE = 'free',
  TRIAL = 'trial',
}

export enum Role {
  ADMIN = 'admin',
  CUSTOMER = 'customer',
  STAFF = 'staff',
}

export interface JWTUser {
  id: string;
  username: string;
  email: string;
  role: Role;
  user_level: UserLevel;
}

interface AuthContextType {
  user: JWTUser | null;
  userId: string | null;
  username: string | null;
  email: string | null;
  user_level: UserLevel | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (usernameOrEmail: string, password: string) => Promise<void>;
  register: (username: string, password: string, email: string) => Promise<void>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<JWTUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        // Нет access_token — но refresh-кука может быть жива.
        // Пробуем поднять сессию через refresh один раз.
        const { data } = await api.post<{ access_token: string }>('/auth/refresh');
        localStorage.setItem('access_token', data.access_token);
      }

      const response = await api.get<JWTUser>('/auth/me');
      setUser(response.data);
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Ошибка fetchUser:', error);
      }
      // Не залогинен: чистим токен, показываем гостя.
      // refresh_token — httponly, из JS не трогаем.
      localStorage.removeItem('access_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (usernameOrEmail: string, password: string) => {
    const response = await api.post<{ access_token: string }>('/auth/login', {
      username_or_email: usernameOrEmail,
      password,
    });
    localStorage.setItem('access_token', response.data.access_token);
    await fetchUser();
  };

  const register = async (username: string, password: string, email: string) => {
    await api.post('/users', { username, password, email });
    await login(username, password);
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    setUser(null);
    // refresh_token — httponly-кука, удаляется бэкендом на logout-эндпоинте.
    // Опционально: api.post('/auth/logout') чтобы бэк сбросил куку.
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextType = {
    user,
    userId: user?.id ?? null,
    username: user?.username ?? null,
    email: user?.email ?? null,
    user_level: user?.user_level ?? null,
    isAuthenticated: !!user,
    loading,
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};